import { useCallback, useMemo } from 'react'
import api, { PROJECT_UUID_HEADER } from '../../lib/commons/api'
import { WbsItemRepository } from '../../applications/ports/wbsItemRepository'
import {
  AddWbsItemAttachmentRequest,
  DeleteWbsItemAttachmentRequest,
  fromApiResponseToEntity,
  UpdateWbsItemDeltaRequest,
  WbsItemType,
} from '../../domain/entity/WbsItemEntity'
import {
  REFERENCED_ENTITY_SEARCH_LIMIT,
  ReferencedEntityWithIcon,
} from '../../domain/value-object/ReferencedEntity'
import { WbsItemTypeVO } from '../../domain/value-object/WbsItemTypeVO'

type WbsItemFindResponseRow = {
  uuid: string
  displayName: string
  typeDto: { iconUrl?: string }
  path?: string
  parentWbsItem?: { uuid: string }
}

const mapWbsItemToReferencedEntity = (
  wbsItem: WbsItemFindResponseRow
): ReferencedEntityWithIcon => ({
  uuid: wbsItem.uuid,
  name: wbsItem.displayName,
  iconUrl: wbsItem.typeDto.iconUrl,
  path: wbsItem.path,
  parentUuid: wbsItem.parentWbsItem?.uuid,
})

export const useWbsItemRepository = (): WbsItemRepository => {
  const getBasicByCode = useCallback(async (code: string) => {
    const response = await api.functional.request(
      'GET',
      '/api/v1/projects/wbs_items/basic',
      { code },
      true
    )
    const basic = response.json
    return {
      ...basic,
      typeVo: new WbsItemTypeVO(basic.typeDto),
    }
  }, [])
  const getDetail = useCallback(async (uuid: string) => {
    const response = await api.functional.request(
      'GET',
      '/api/v1/projects/wbs_items/detail',
      { wbsItemUuid: uuid },
      true
    )
    return fromApiResponseToEntity(response.json)
  }, [])
  const searchAsReferencedEntity = useCallback(
    async (
      projectUuid: string,
      rootWbsItemTypes: WbsItemType[] | undefined,
      searchText: string
    ): Promise<ReferencedEntityWithIcon[]> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/wbs_items/referenced_values/find',
        {
          projectUuid,
          types: rootWbsItemTypes,
          all: searchText,
          limit: REFERENCED_ENTITY_SEARCH_LIMIT,
        },
        true
      )
      return response.json.data as ReferencedEntityWithIcon[]
    },
    []
  )
  const fetchAsReferencedEntity = useCallback(
    async (uuid: string): Promise<ReferencedEntityWithIcon | undefined> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/wbs_items/referenced_values',
        { uuid },
        true
      )
      return response.json
    },
    []
  )
  const fetchListAsReferencedEntity = useCallback(
    async (
      projectUuid: string,
      rootWbsItemTypes: WbsItemType[] | undefined
    ): Promise<ReferencedEntityWithIcon[]> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/wbs_items/find',
        { projectUuid, types: rootWbsItemTypes },
        true
      )
      return response.json.data.map(mapWbsItemToReferencedEntity)
    },
    []
  )
  // fetchAsReferencedEntity(/referenced_values)は親情報・パスを返さないため、
  // ツリー表示で親子関係を必要とする呼び出し元向けに、/findをuuid指定（完全一致）
  // で呼び、fetchListAsReferencedEntityと同じ形で親情報も含めて解決する
  const fetchAsReferencedEntityByUuid = useCallback(
    async (
      projectUuid: string,
      uuid: string
    ): Promise<ReferencedEntityWithIcon | undefined> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/wbs_items/find',
        { projectUuid, uuid },
        true
      )
      const [wbsItem] = response.json.data
      return wbsItem ? mapWbsItemToReferencedEntity(wbsItem) : undefined
    },
    []
  )
  const updateDelta = useCallback(
    async (
      projectUuid: string,
      wbsItemType: WbsItemTypeVO,
      request: UpdateWbsItemDeltaRequest
    ) => {
      const endPoint = (() => {
        if (wbsItemType.isTicketList()) {
          return '/api/v1/projects/wbs_items/delta/ticket_list'
        } else if (wbsItemType.isTicket()) {
          return '/api/v1/projects/wbs_items/delta/ticket'
        }
        switch (request.input.type) {
          case WbsItemType.PROCESS:
            return '/api/v1/projects/wbs_items/delta/process'
          case WbsItemType.DELIVERABLE_LIST:
            return '/api/v1/projects/wbs_items/delta/deliverable_list'
          case WbsItemType.DELIVERABLE:
            return '/api/v1/projects/wbs_items/delta/deliverable'
          case WbsItemType.TASK:
            return '/api/v1/projects/wbs_items/delta/task'
        }
        return ''
      })()
      const response = await api.functional.request(
        'PUT',
        endPoint,
        request,
        true,
        null,
        { [PROJECT_UUID_HEADER]: projectUuid }
      )
      return { response }
    },
    []
  )
  const addAttachments = useCallback(
    async (projectUuid: string, request: AddWbsItemAttachmentRequest) => {
      const response = await api.functional.request(
        'POST',
        '/api/v1/projects/wbs_items/attachments/items/' +
          (request.type.isTicket() ? 'ticket' : 'wbs_item'),
        request,
        true,
        null,
        { [PROJECT_UUID_HEADER]: projectUuid }
      )
      return response.json
    },
    []
  )
  const deleteAttachments = useCallback(
    async (projectUuid: string, request: DeleteWbsItemAttachmentRequest) => {
      const response = await api.functional.request(
        'DELETE',
        '/api/v1/projects/wbs_items/attachments/items/' +
          (request.type.isTicket() ? 'ticket' : 'wbs_item'),
        request,
        true,
        null,
        { [PROJECT_UUID_HEADER]: projectUuid }
      )
      return response.json
    },
    []
  )
  return useMemo(
    () => ({
      getBasicByCode,
      getDetail,
      searchAsReferencedEntity,
      fetchAsReferencedEntity,
      fetchAsReferencedEntityByUuid,
      fetchListAsReferencedEntity,
      updateDelta,
      addAttachments,
      deleteAttachments,
    }),
    [
      getBasicByCode,
      getDetail,
      searchAsReferencedEntity,
      fetchAsReferencedEntity,
      fetchAsReferencedEntityByUuid,
      fetchListAsReferencedEntity,
      updateDelta,
      addAttachments,
      deleteAttachments,
    ]
  )
}
